// Turns a brawl into text people can keep: one column as plain text for the
// clipboard, or the whole brawl as a Markdown file.
import { IDS } from "../lib/protocol.js";

const phaseLabel = (t) => (t.phase === "opening" ? "Opening (blind)" : t.phase === "final" ? "Final answer" : `Round ${t.round}`);

// A question's web sources, numbered as the models cite them: from `firstSource`,
// counted across the session.
const numbered = (sources, firstSource) => sources?.results.map((r, i) => ({ n: firstSource + i, ...r })) ?? [];

// What one column shows: the question, its web sources, the notes everyone saw, and
// that seat's own turns, each with its tags (VERDICT, ENDORSE, CONFIDENCE) as the
// model wrote them.
export function columnText(seat, { question, transcript, names, sources = null, firstSource = 1 }) {
  const parts = [`Question: ${question}`];
  const list = numbered(sources, firstSource);
  if (list.length) parts.push(`Web sources (searched ${sources.date} for "${sources.query}"):\n${list.map((s) => `[${s.n}] ${s.title}: ${s.url}`).join("\n")}`);
  for (const t of transcript) {
    if (t.speaker === "MODERATOR") parts.push(`Moderator: ${t.text}`);
    else if (t.speaker === "JUDGE") parts.push(`Judge: ${t.text}`);
    else if (t.speaker === seat) parts.push(`${names[seat]} · ${phaseLabel(t)}\n${t.text.trim()}`);
  }
  return parts.join("\n\n");
}

// Markdown table cells can't hold pipes or line breaks.
const cell = (s) => String(s ?? "").replace(/\|/g, "\\|").replace(/\s*\n\s*/g, " ");

// The whole brawl, in the order it happened, then the vote board and the verdict.
//   board: [{ round, votes: { SEAT: { endorse, confidence, switched } }, result }]
//   verdict: { meta, speaker } or null while there's no final answer
//   sources: the question's web search ({ date, query, results }), numbered from firstSource
export function brawlMarkdown({ question, transcript, names, labels, board = [], verdict = null, sources = null, firstSource = 1, date = new Date() }) {
  const title = question.split("\n")[0].slice(0, 80);
  const out = [
    `# Chat Brawl: ${title}`,
    "",
    `${date.toISOString().slice(0, 10)} · ${IDS.map((id) => `${names[id]} (${labels[id]})`).join(" · ")}`,
    "",
    "## Question",
    "",
    question,
  ];
  const list = numbered(sources, firstSource);
  if (list.length) {
    out.push("", "## Web sources", "", `Searched on ${sources.date} for "${sources.query}" (Brave Search). Snippets as the models saw them.`, "");
    const link = (s) => `[${s.title.replace(/[[\]\\]/g, "\\$&")}](${s.url.replace(/[()\s]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")}`)})`;
    for (const s of list) out.push(`${s.n}. ${link(s)}${s.age ? ` (${s.age})` : ""}: ${s.snippet}`);
  }
  let section = null;
  for (const t of transcript) {
    if (t.speaker === "MODERATOR" || t.speaker === "JUDGE") {
      out.push("", `> **${t.speaker === "JUDGE" ? "Judge" : "Moderator"}:** ${t.text.replace(/\n/g, "\n> ")}`);
      continue;
    }
    if (t.phase === "final") continue; // goes last, with the verdict
    const label = phaseLabel(t);
    if (label !== section) {
      out.push("", `## ${label}`);
      section = label;
    }
    out.push("", `### ${names[t.speaker]}`, "", t.text.trim());
  }
  if (board.length) {
    out.push("", "## Vote board", "", `| Round | ${IDS.map((id) => cell(names[id])).join(" | ")} | Result |`, `|---|${IDS.map(() => "---").join("|")}|---|`);
    for (const row of board) {
      const votes = IDS.map((id) => {
        const v = row.votes[id];
        if (!v) return "";
        if (!v.endorse) return "no vote";
        return `${names[v.endorse]}${v.endorse === id ? " (self)" : ""}${v.confidence != null ? ` · ${v.confidence}% sure` : ""}${v.switched ? " ↻" : ""}`;
      });
      out.push(`| ${row.round} | ${votes.map(cell).join(" | ")} | ${cell(row.result)} |`);
    }
  }
  const final = transcript.find((t) => t.phase === "final");
  if (final) {
    out.push("", "## Final answer", "", `*${verdict?.meta ?? `Spoken by ${names[final.speaker]}`}*`, "", final.text.trim());
  }
  return out.join("\n") + "\n";
}

// A file name like chat-brawl-2026-10-24-postgres-or-mongodb.md
export function exportFileName(question, date = new Date()) {
  const slug = question
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/, "");
  return `chat-brawl-${date.toISOString().slice(0, 10)}${slug ? `-${slug}` : ""}.md`;
}

// What Share sends to a group chat: the question, who won and how, the final answer
// (plain text, cut at a sentence near `max` characters), and a link that opens the
// site with the question already in the box.
export function shareText({ question, winner, how, answer, origin, max = 700 }) {
  let plain = answer
    .replace(/^\s*\**(VERDICT|ENDORSE|CONFIDENCE)\**\s*:.*$/gim, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\s*\[\d{1,3}(?:,\s*\d{1,3})*\]/g, "") // citations point at sources the reader can't see
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  if (plain.length > max) {
    const cut = plain.slice(0, max);
    const end = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf(".\n"));
    plain = `${end > max / 2 ? cut.slice(0, end + 1) : cut.trimEnd()}…`;
  }
  const q = question.split("\n")[0].trim();
  const link = `${origin}/?q=${encodeURIComponent(q.slice(0, 200))}&utm_source=share`;
  return {
    text: `I asked three AIs: “${q}”\n\n🏆 ${winner} won (${how}).\n\n${plain}\n\nBrawl your own question:`,
    url: link,
  };
}
